import { describeError, useApi } from '@classgo/app-kit';
import type { AttendanceExcuse, ExcuseStatus } from '@classgo/shared';
import { Badge, type BadgeTone, Button, EmptyState, Modal, Select, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { attendanceApi, attendanceQueries, refusalOf } from '../../attendance/api';
import type { ExcuseFilter } from '../../attendance/types';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';
import type { ExcuseReview } from './types';

const TONES: Record<ExcuseStatus, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
};

const FILTERS: readonly ExcuseFilter[] = ['all', 'pending', 'approved', 'rejected'];

/**
 * The review queue (#114 over #105): the families' excuses with the pending ones first, each
 * with its dates, reason and file; with `attendance:write`, "Aceptar" or "Rechazar" with a note
 * for the family. Accepting turns the child's absences in those dates into excused ones; the
 * decision is final, so a contradicting one is refused by the API and shown in the dialog.
 */
export const ExcusesTab = ({ canWrite }: { canWrite: boolean }) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<ExcuseFilter>('all');
  const excuses = useQuery(attendanceQueries.excuses(api, filter));
  const [review, setReview] = useState<ExcuseReview | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setReview(null);
    setNote('');
    setError(null);
  };
  const decide = useMutation({
    mutationFn: (input: ExcuseReview) =>
      attendanceApi(api).review(input.excuse.id, {
        decision: input.decision,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: async (result) => {
      close();
      toast.show({
        tone: 'success',
        title:
          result.status === 'approved'
            ? t('attendance.review.approved', { count: result.excusedMarks })
            : t('attendance.review.rejected'),
      });
      await queries.invalidateQueries({ queryKey: ['school', 'attendance'] });
    },
    onError: (thrown) => setError(refusalOf(thrown) ?? describeError(t, thrown)),
  });
  const openFile = async (excuse: AttendanceExcuse) => {
    // The window opens in the click, before the fetch, so the browser does not block it.
    const popup = window.open('about:blank', '_blank');
    try {
      const link = await attendanceApi(api).attachment(excuse.id);
      if (popup) popup.location.href = link.url;
      else window.location.assign(link.url);
    } catch {
      popup?.close();
      toast.show({ tone: 'error', title: t('attendance.excuse.fileFailed') });
    }
  };
  const items = excuses.data?.items ?? [];
  const student = review?.excuse.studentName ?? '';

  return (
    <div className="space-y-4">
      <div className="max-w-xs">
        <Select
          label={t('attendance.excuses.filter')}
          value={filter}
          onChange={(event) => setFilter(event.target.value as ExcuseFilter)}
          options={FILTERS.map((value) => ({
            value,
            label:
              value === 'all'
                ? t('attendance.excuses.all')
                : t(`attendance.excuse.status.${value}`),
          }))}
        />
      </div>
      {excuses.isError ? (
        <EmptyState title={t('entity.error.title')} description={describeError(t, excuses.error)} />
      ) : excuses.isPending ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <EmptyState
          title={
            filter === 'pending'
              ? t('attendance.excuses.emptyPending')
              : t('attendance.excuses.empty')
          }
        />
      ) : (
        <ul aria-label={t('attendance.excuses.list')} className="space-y-3">
          {items.map((excuse) => (
            <li key={excuse.id} className="rounded-control border border-gray-200 p-3 sm:p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{excuse.studentName ?? '—'}</p>
                  <p className="text-sm text-gray-600">
                    {t('attendance.excuse.range', {
                      from: formatDate(excuse.startsOn),
                      to: formatDate(excuse.endsOn),
                    })}
                  </p>
                </div>
                <Badge tone={TONES[excuse.status]}>
                  {t(`attendance.excuse.status.${excuse.status}`)}
                </Badge>
              </div>
              <p className="mt-2 text-sm text-gray-900">{excuse.reason}</p>
              <p className="mt-1 text-xs text-gray-600">
                {t('attendance.excuse.submitted', {
                  name: excuse.submittedByName ?? '—',
                  date: formatDate(excuse.submittedAt),
                })}
              </p>
              {excuse.reviewedAt ? (
                <p className="text-xs text-gray-600">
                  {t('attendance.excuse.reviewed', {
                    name: excuse.reviewedByName ?? '—',
                    date: formatDate(excuse.reviewedAt),
                  })}
                  {excuse.reviewNote ? ` · ${excuse.reviewNote}` : ''}
                </p>
              ) : null}
              {excuse.attachment || (canWrite && excuse.status === 'pending') ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {excuse.attachment ? (
                    <Button variant="secondary" size="sm" onClick={() => void openFile(excuse)}>
                      {t('attendance.excuse.file', { filename: excuse.attachment.filename })}
                    </Button>
                  ) : null}
                  {canWrite && excuse.status === 'pending' ? (
                    <>
                      <Button size="sm" onClick={() => setReview({ excuse, decision: 'approved' })}>
                        {t('attendance.excuse.approve')}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setReview({ excuse, decision: 'rejected' })}
                      >
                        {t('attendance.excuse.reject')}
                      </Button>
                    </>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <Modal
        open={review !== null}
        onClose={close}
        title={
          review
            ? t(`attendance.review.${review.decision}.title`, { student })
            : t('attendance.tab.excuses')
        }
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={review?.decision === 'rejected' ? 'danger' : 'primary'}
              loading={decide.isPending}
              onClick={() => {
                if (review) decide.mutate(review);
              }}
            >
              {review?.decision === 'rejected'
                ? t('attendance.excuse.reject')
                : t('attendance.excuse.approve')}
            </Button>
          </>
        }
      >
        {review ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              {review.decision === 'approved'
                ? t('attendance.review.approve.body', {
                    student,
                    from: formatDate(review.excuse.startsOn),
                    to: formatDate(review.excuse.endsOn),
                  })
                : t('attendance.review.reject.body')}
            </p>
            <div>
              <label htmlFor="review-note" className="block text-sm font-medium text-gray-900">
                {t('attendance.review.note')}
              </label>
              <textarea
                id="review-note"
                rows={3}
                maxLength={500}
                className="mt-1 w-full rounded-control border border-gray-300 p-2 text-sm"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
            {error ? (
              <p
                role="alert"
                className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
              >
                {error}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};
